import clsx from "clsx";
import {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { DATA_E2E_KEY, onActivateKeyDown } from "@ryde/shared";

import { CollapsableControlContainer } from "./collapsable-control-container";
import { CollapseIcon } from "./collapse-icon";
import { CollapseStyled } from "./collapse.styled";

interface ICollapseProps extends PropsWithChildren {
  maxHeight?: number;
  isInner?: boolean;
  isSkipMaxHeight?: boolean;
  CollapseHeader: ReactNode;
  forceCollapsed?: boolean;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
}

export const Collapse = ({
  maxHeight,
  isSkipMaxHeight,
  isInner,
  CollapseHeader,
  children,
  forceCollapsed,
  isCollapsed: externalIsCollapsed,
  onToggleCollapsed,
}: ICollapseProps): ReactElement => {
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const prevForceCollapsed = useRef<boolean | undefined>(undefined);

  const isCollapsed =
    externalIsCollapsed !== undefined
      ? externalIsCollapsed
      : internalIsCollapsed;

  const toggleCollapse = (): void => {
    if (onToggleCollapsed) {
      onToggleCollapsed();
    } else {
      setInternalIsCollapsed(prevState => !prevState);
    }
  };

  useEffect(() => {
    // Trigger collapse only when forceCollapsed goes from false to true
    if (forceCollapsed && !prevForceCollapsed.current) {
      if (onToggleCollapsed) {
        // If externally controlled, we can't force collapse
        // The parent should handle this
      } else {
        setInternalIsCollapsed(true);
      }
    }

    prevForceCollapsed.current = forceCollapsed;
  }, [forceCollapsed, onToggleCollapsed]);

  return (
    <CollapseStyled className={clsx({ "CollapseStyled--border": !isInner })}>
      <header
        className={clsx("Collapse-Header", {
          "Collapse-Header-Inner": isInner,
        })}
        onClick={toggleCollapse}
        onKeyDown={onActivateKeyDown(toggleCollapse)}
        role="button"
        tabIndex={0}
        {...{ [DATA_E2E_KEY]: "collapse-header" }}
      >
        {CollapseHeader}
        <CollapseIcon isCollapsed={isCollapsed} />
      </header>
      <CollapsableControlContainer
        maxHeight={maxHeight}
        isSkipMaxHeight={isSkipMaxHeight}
        isCollapsed={isCollapsed}
      >
        {children}
      </CollapsableControlContainer>
    </CollapseStyled>
  );
};
